import {
  Children,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import clsx from "clsx";
import {
  masonryPositions,
  reconcileMasonryColumns,
  type MasonryColumns,
} from "@/projectOverview/overviewMasonryLayout";

/** Modal-Übersicht: gap 0.75rem */
const MODAL_GAP_PX = 12;
/** Projektseite: etwas mehr Luft zwischen den Kacheln */
const PAGE_GAP_PX = 20;
/**
 * Drei Spalten erst, wenn jede Kachel noch mindestens ~28rem breit ist.
 * Darunter bleiben die Infos in zwei breiteren Spalten lesbar.
 */
const PAGE_COL_MIN_PX = 28 * 16;

type LayoutState = {
  columnCount: number;
  colWidth: number;
  heights: number[];
  columns: MasonryColumns;
  keys: string[];
};

function masonryGap(pageLayout: boolean): number {
  return pageLayout ? PAGE_GAP_PX : MODAL_GAP_PX;
}

function resolveColumnCount(pageLayout: boolean, containerWidth: number): number {
  if (typeof window === "undefined") return 1;
  if (window.matchMedia("(max-width: 767.98px)").matches) return 1;
  if (!pageLayout) return 2;
  if (!window.matchMedia("(min-width: 1024px)").matches) return 1;
  const gap = masonryGap(true);
  const threeColMin = PAGE_COL_MIN_PX * 3 + gap * 2;
  return containerWidth >= threeColMin ? 3 : 2;
}

/** Unter einem Pixel ist Messrauschen (Subpixel, Scrollbar), kein Inhaltswechsel. */
const HEIGHT_EPSILON_PX = 1;

function listsEqual(a: number[], b: number[], epsilon: number): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (Math.abs(a[i]! - b[i]!) > epsilon) return false;
  }
  return true;
}

function keysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function itemKey(item: ReactElement, index: number): string {
  return item.key != null ? String(item.key) : `idx:${index}`;
}

/**
 * Platzsparendes Kachel-Raster. Kleine Höhenänderungen bleiben in der Spalte,
 * damit eine nachladende Kachel das Raster nicht bei jedem Pixel versetzt.
 * Verschwindende Kacheln und ein stark ungleiches Raster werden neu gepackt.
 */
export function OverviewMasonry({
  children,
  className,
  pageLayout = false,
}: {
  children: ReactNode;
  className?: string;
  pageLayout?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<number, HTMLDivElement>());
  const [layout, setLayout] = useState<LayoutState>(() => ({
    columnCount:
      typeof window !== "undefined" ? resolveColumnCount(pageLayout, window.innerWidth) : 1,
    colWidth: 0,
    heights: [],
    columns: [],
    keys: [],
  }));

  const items = useMemo(
    () => Children.toArray(children).filter(Boolean) as ReactElement[],
    [children],
  );

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const sync = () => {
      const width = root.clientWidth;
      if (width <= 0) return;

      const host = root.closest(".eh-project-page-main") as HTMLElement | null;
      const gap = masonryGap(pageLayout);
      const columnCount = resolveColumnCount(pageLayout, host?.clientWidth ?? width);
      const colWidth = (width - gap * (columnCount - 1)) / columnCount;

      const heights: number[] = [];
      const keys: string[] = [];
      for (let i = 0; i < items.length; i++) {
        const el = itemRefs.current.get(i);
        heights[i] = el ? el.offsetHeight : 0;
        keys[i] = itemKey(items[i]!, i);
      }

      setLayout((prev) => {
        const columns = reconcileMasonryColumns(
          prev.columns,
          prev.keys,
          prev.columnCount,
          keys,
          heights,
          columnCount,
        );
        if (
          prev.columnCount === columnCount &&
          Math.abs(prev.colWidth - colWidth) < 0.5 &&
          listsEqual(prev.heights, heights, HEIGHT_EPSILON_PX) &&
          listsEqual(prev.columns, columns, 0) &&
          keysEqual(prev.keys, keys)
        ) {
          return prev;
        }
        return { columnCount, colWidth, heights, columns, keys };
      });
    };

    sync();

    const ro = new ResizeObserver(() => sync());
    ro.observe(root);
    const host = root.closest(".eh-project-page-main");
    if (host) ro.observe(host);
    itemRefs.current.forEach((el) => ro.observe(el));

    const mqMobile = window.matchMedia("(max-width: 767.98px)");
    const mqPage = window.matchMedia("(min-width: 1024px)");
    const onMq = () => sync();
    mqMobile.addEventListener("change", onMq);
    mqPage.addEventListener("change", onMq);

    return () => {
      ro.disconnect();
      mqMobile.removeEventListener("change", onMq);
      mqPage.removeEventListener("change", onMq);
    };
    // colWidth/columnCount: zweite Messung nach Breiten-Update (Umbruch ändert Höhen)
  }, [items, pageLayout, layout.columnCount, layout.colWidth]);

  const gap = masonryGap(pageLayout);
  const { positions, totalHeight } = useMemo(
    () =>
      masonryPositions(
        layout.heights,
        layout.columns,
        layout.columnCount,
        layout.colWidth,
        gap,
      ),
    [layout.heights, layout.columns, layout.columnCount, layout.colWidth, gap],
  );

  const ready = layout.colWidth > 0 && layout.heights.length === items.length;

  return (
    <div
      ref={rootRef}
      className={clsx("eh-po-masonry", className)}
      data-ready={ready ? "true" : "false"}
      data-cols={String(layout.columnCount || 1)}
      style={
        {
          "--eh-po-masonry-cols": String(layout.columnCount || 1),
          "--eh-po-masonry-gap": `${gap}px`,
          height: ready ? totalHeight : undefined,
        } as CSSProperties
      }
    >
      {items.map((item, index) => {
        const pos = positions[index];
        const visible = Boolean(pos?.visible);
        const style: CSSProperties = {
          width: layout.colWidth > 0 ? layout.colWidth : undefined,
        };
        if (ready && visible && pos) {
          style.transform = `translate(${pos.left}px, ${pos.top}px)`;
        } else if (ready && !visible) {
          // Leer (z. B. Angebote ohne Daten), aber messbar halten falls Inhalt nachkommt
          style.visibility = "hidden";
          style.pointerEvents = "none";
        }

        return (
          <div
            key={item.key ?? index}
            className="eh-po-masonry__item"
            style={style}
            ref={(el) => {
              if (el) itemRefs.current.set(index, el);
              else itemRefs.current.delete(index);
            }}
          >
            {item}
          </div>
        );
      })}
    </div>
  );
}
